import { Component, inject, signal, OnInit, ViewChild, AfterViewInit, DestroyRef, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ReactiveFormsModule,
  FormBuilder,
  Validators,
  ValidationErrors,
  AbstractControl,
  ValidatorFn
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, concatMap, debounceTime, distinctUntilChanged, finalize, from, map, of, switchMap, toArray } from 'rxjs';
import { Router } from '@angular/router';

import { SourceService } from '../../../service/source.service';
import { Municipality } from '../../../service/municipality.service';
import { GeneralService } from '../../../service/general.service';
import { CodigoPostalService } from '../../../service/codigo-postal.service';
import { SourceContactsService } from '../../../service/source-contacts.service';
import { SourceSurveyService } from '../../../service/source-survey';

import { DepartmentsGT } from '../../../enum/departments.enum';
import { MunicipalityGT } from '../../../enum/municipalities.enum';

import { SourceContact } from '../../../models/SourceContact';
import { BaseContact } from '../../../models/base-contact';
import { SourceArticle, SurveyArticulo } from '../../article-view/source-article/source-article';
import { SourceSurveyPayload, SourceSurveyResponse } from '../../../models/source-survey.models';
import { SuccessAlertModalComponent } from '../../../shared/components/success-alert-modal/success-alert-modal';
import { ValidationSummaryModalComponent } from '../../../shared/components/validation-summary-modal/validation-summary-modal';
import { SourceArticlesService } from '../../../features/source-articles/services/source-articles.service';
import { SourceArticleCharacteristicsService } from '../../../features/source-article-characteristics/services/source-article-characteristics.service';

const DEFAULT_EMAIL = 'nd@nd.com';
const DEFAULT_JORNADAS = {
  horaInicioPrimeraJornada: '08:00',
  horaFinalPrimeraJornada: '12:00',
  horaInicioSegundaJornada: '13:00',
  horaFinalSegundaJornada: '16:00'
};
const PHONE_TEXT_PATTERN = /^[A-Za-z0-9áéíóúÁÉÍÓÚñÑ\s()+\-./#]*$/;
const NIT_TEXT_PATTERN = /^[A-Za-z0-9\s/-]*$/;

interface CodigoPostalQuery {
  depId: number;
  municipioId: number;
  referencia: string;
}

interface CodigoPostalLookupResult {
  lista: any[];
  preserveCurrentState: boolean;
}

interface MobileSelectOption {
  value: string | number;
  label: string;
}

type MobileSelectControlName =
  | 'fkDepartamento'
  | 'fkMunicipio'
  | 'fkUsuario'
  | 'periodo'
  | 'fkPeriodicidadVisita'
  | 'fkDiaVisita'
  | 'requiereAudio'
  | 'requiereImagen'
  | 'fkActividadEmpresa'
  | 'fkTamanoEmpresa'
  | 'estado';

const jornadasCongruentesValidator: ValidatorFn = (
  group: AbstractControl
): ValidationErrors | null => {
  const h1i = group.get('horaInicioPrimeraJornada')?.value;
  const h1f = group.get('horaFinalPrimeraJornada')?.value;
  const h2i = group.get('horaInicioSegundaJornada')?.value;
  const h2f = group.get('horaFinalSegundaJornada')?.value;

  const toMinutes = (t: string) => {
    if (!t || !t.includes(':')) return NaN;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  const errors: ValidationErrors = {};

  if (h1i && h1f) {
    const i1 = toMinutes(h1i);
    const f1 = toMinutes(h1f);
    if (!isNaN(i1) && !isNaN(f1) && f1 <= i1) {
      errors['primeraJornadaInvalida'] = true;
    }
  }

  if (h2i && h2f) {
    const i2 = toMinutes(h2i);
    const f2 = toMinutes(h2f);
    if (!isNaN(i2) && !isNaN(f2) && f2 <= i2) {
      errors['segundaJornadaInvalida'] = true;
    }

    if (h1f) {
      const f1 = toMinutes(h1f);
      if (!isNaN(i2) && !isNaN(f1) && i2 < f1) {
        errors['traslapeJornadas'] = true;
      }
    }
  }

  return Object.keys(errors).length ? errors : null;
};

@Component({
  selector: 'app-add-source',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SourceArticle, SuccessAlertModalComponent, ValidationSummaryModalComponent],
  templateUrl: './add-source.html',
  styleUrls: ['./add-source.scss'],
})
export class AddSource implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly codigoPostalQuery$ = new Subject<CodigoPostalQuery>();

  ngAfterViewInit(): void { }

  @ViewChild(SourceArticle)
  articleCmp?: SourceArticle;

  private fb = inject(FormBuilder);
  private sourceService = inject(SourceService);
  private municipalityService = inject(Municipality);
  private generalService = inject(GeneralService);
  private codigoPostalService = inject(CodigoPostalService);
  private sourceContactService = inject(SourceContactsService);
  private sourceArticlesService = inject(SourceArticlesService);
  private sourceArticleCharacteristicsService = inject(SourceArticleCharacteristicsService);
  private surveyService = inject(SourceSurveyService);
  private router = inject(Router);

  constructor() {
    this.setupCodigoPostalLookup();
  }


  private getLoggedUserId(): number | null {
    const raw = localStorage.getItem('authUser');
    if (!raw) return null;

    try {
      const user = JSON.parse(raw);
      return Number(user.pkUsuario) || null;
    } catch {
      return null;
    }
  }

  pendingContacts: SourceContact[] = [];
  editingIndex: number | null = null;

  usuarios = signal<any[]>([]);
  periodicidades = signal<any[]>([]);
  actividades = signal<any[]>([]);
  tamanios = signal<any[]>([]);
  diasVisita = signal<any[]>([]);
  editingContactIndex: number | null = null;
  showContactForm = signal(false);
  readonly mobileSelectOpen = signal(false);
  readonly mobileSelectTitle = signal('');
  readonly mobileSelectControl = signal<MobileSelectControlName | null>(null);
  readonly mobileSelectOptions = signal<MobileSelectOption[]>([]);
  readonly mobileSelectQuery = signal('');
  readonly filteredMobileSelectOptions = computed(() => {
    const query = this.normalizeSearchText(this.mobileSelectQuery());
    const options = this.mobileSelectOptions();

    if (!query) {
      return options;
    }

    return options.filter((option) =>
      this.normalizeSearchText(option.label).includes(query)
    );
  });
  readonly yesNoOptions: MobileSelectOption[] = [
    { value: 'S', label: 'Sí' },
    { value: 'N', label: 'No' }
  ];
  readonly statusOptions: MobileSelectOption[] = [
    { value: 'A', label: 'Activo' },
    { value: 'P', label: 'Pendiente' },
    { value: 'I', label: 'Inactivo' }
  ];

  departaments = Object.entries(DepartmentsGT)
    .filter(([_, v]) => !isNaN(Number(v)))
    .map(([k, v]) => ({ id: Number(v), nombre: k }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));

  municipalities = signal<{ id: number; nombre: string }[]>([]);

  codigosPostales = signal<any[]>([]);
  codigoPostalDescripcion = '';

  periodoTitulo = signal<string>('Periodo');
  periodoLimite = signal<number>(12);

  loading = signal(false);
  geoLoading = signal(false);
  generatingNumber = signal(false);
  success = signal(false);
  showValidationModal = signal(false);
  successMessage = signal('La fuente se subió exitosamente.');
  error = signal<string | null>(null);
  createdSourceId = signal<number | null>(null);

  missingFields = signal<string[]>([]);

  form = this.fb.group({
    codigoINE: ['', Validators.required],
    razonSocial: ['', Validators.required],
    nombre: ['', Validators.required],
    direccion: ['', Validators.required],

    fkCodigoPostal: this.fb.control<number | null>(null, Validators.required),
    fkDepartamento: [null, Validators.required],
    fkMunicipio: [null, Validators.required],
    fkUsuario: [null, Validators.required],
    fkPeriodicidadVisita: [null, Validators.required],
    fkDiaVisita: [null, Validators.required],

    periodo: [null, Validators.required],
    ordenRuta: this.fb.control<number | null>(0, [Validators.min(0)]),

    zona: [null],

    gpsLatitud: this.fb.control<number | null>(null),
    gpsLongitud: this.fb.control<number | null>(null),
    gpsAltitud: this.fb.control<number | null>(null),
    gpsFecha: this.fb.control<string | null>(null),

    telefonos: ['', [Validators.pattern(PHONE_TEXT_PATTERN)]],
    email: ['', [Validators.email]],

    horaInicioPrimeraJornada: [DEFAULT_JORNADAS.horaInicioPrimeraJornada],
    horaFinalPrimeraJornada: [DEFAULT_JORNADAS.horaFinalPrimeraJornada],
    horaInicioSegundaJornada: [DEFAULT_JORNADAS.horaInicioSegundaJornada],
    horaFinalSegundaJornada: [DEFAULT_JORNADAS.horaFinalSegundaJornada],

    requiereAudio: ['N'],
    requiereImagen: ['N'],
    nit: ['', [Validators.required, Validators.pattern(NIT_TEXT_PATTERN)]],
    fkActividadEmpresa: [null, Validators.required],
    fkTamanoEmpresa: [null, Validators.required],
    codigoCIIU: [''],
    comentarios: [''],
    referencia: [''],
    informacionAdicional: [''],
    estado: ['P'],
  }, {
    validators: jornadasCongruentesValidator
  });

  private readonly labels: Record<string, string> = {
    codigoINE: 'Código INE',
    razonSocial: 'Razón Social',
    nombre: 'Nombre',
    direccion: 'Dirección',
    fkCodigoPostal: 'Código Postal',
    fkDepartamento: 'Departamento',
    fkMunicipio: 'Municipio',
    fkUsuario: 'Usuario Responsable',
    fkPeriodicidadVisita: 'Periodicidad',
    fkDiaVisita: 'Día de Visita',
    periodo: 'Periodo',
    ordenRuta: 'Orden de Ruta',
    nit: 'NIT',
    fkActividadEmpresa: 'Actividad Empresa',
    fkTamanoEmpresa: 'Tamaño Empresa',
  };

  ngOnInit(): void {
    this.selectFirstDepartmentIfNeeded();
    this.loadCatalogs();

    if (!this.form.get('gpsFecha')?.value) {
      this.form.patchValue({ gpsFecha: this.nowLocalDateTime() }, { emitEvent: false });
    }

  }

  private setupCodigoPostalLookup(): void {
    this.codigoPostalQuery$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(
          (previous, current) =>
            previous.depId === current.depId &&
            previous.municipioId === current.municipioId &&
            previous.referencia === current.referencia
        ),
        switchMap(({ depId, municipioId, referencia }) =>
          this.codigoPostalService.getCodigosPostales(depId, municipioId, referencia).pipe(
            map((lista): CodigoPostalLookupResult => ({
              lista,
              preserveCurrentState: false
            })),
            catchError((error) => {
              if (this.isTransientCodigoPostalError(error)) {
                return of({
                  lista: this.codigosPostales(),
                  preserveCurrentState: true
                });
              }

              return of({
                lista: [],
                preserveCurrentState: false
              });
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ lista, preserveCurrentState }) => {
        if (preserveCurrentState) {
          return;
        }

        this.codigosPostales.set(lista);

        if (lista.length > 0) {
          this.form.patchValue({ fkCodigoPostal: lista[0].pk });
          this.codigoPostalDescripcion = lista[0].descripcion;
        } else {
          this.form.patchValue({ fkCodigoPostal: null });
          this.codigoPostalDescripcion = '';
        }
      });
  }

  private isTransientCodigoPostalError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && (error.status === 0 || error.status === 502);
  }

  private normalizeSelectValue(value: unknown): string | null {
    if (value === null || value === undefined || value === '') return null;
    return String(value);
  }

  private setFirstFormOptionIfNeeded<T>(
    controlName: string,
    list: T[],
    getValue: (item: T) => unknown
  ): unknown {
    const control = this.form.get(controlName);
    if (!control) return null;

    const currentValue = this.normalizeSelectValue(control.value);
    const hasCurrentValue = currentValue != null && list.some(
      item => this.normalizeSelectValue(getValue(item)) === currentValue
    );

    if (hasCurrentValue) {
      return control.value;
    }

    const nextValue = list.length > 0 ? getValue(list[0]) : null;
    control.patchValue((nextValue ?? null) as any, { emitEvent: false });
    return nextValue;
  }

  private normalizeCatalogText(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  private prioritizeMensualPeriodicidad(list: any[]): any[] {
    const mensualIndex = list.findIndex(item =>
      this.normalizeCatalogText(item?.descripcion) === 'mensual'
    );

    if (mensualIndex <= 0) {
      return list;
    }

    const mensual = list[mensualIndex];
    return [mensual, ...list.filter((_, index) => index !== mensualIndex)];
  }

  private selectMensualPeriodicidadIfNeeded(list: any[]): void {
    const orderedList = this.prioritizeMensualPeriodicidad(list);
    this.periodicidades.set(orderedList);
    this.setFirstFormOptionIfNeeded('fkPeriodicidadVisita', orderedList, item => item?.pk);
  }

  private selectFirstDepartmentIfNeeded(): void {
    const selectedDepartment = this.setFirstFormOptionIfNeeded(
      'fkDepartamento',
      this.departaments,
      item => item.id
    );

    if (selectedDepartment != null) {
      this.onDepartamentoChange();
    }
  }

  private selectFirstPeriodoIfNeeded(): void {
    this.setFirstFormOptionIfNeeded(
      'periodo',
      this.periodos(),
      item => item
    );
  }

  loadCatalogs(): void {
    this.generalService.get('GET_USERS').subscribe(r => {
      const list = r ?? [];
      this.usuarios.set(list);
      this.setFirstFormOptionIfNeeded('fkUsuario', list, item => item?.pk);
    });

    this.generalService.get('GET_PERIODICIDAD_VISITA').subscribe(r => {
      const list = r ?? [];
      this.selectMensualPeriodicidadIfNeeded(list);
    });

    this.generalService.get('GET_ACTIVIDADES_EMPRESA').subscribe(r => {
      const list = r ?? [];
      this.actividades.set(list);
      this.setFirstFormOptionIfNeeded('fkActividadEmpresa', list, item => item?.pk);
    });

    this.generalService.get('GET_TAMANO_EMPRESA').subscribe(r => {
      const list = r ?? [];
      this.tamanios.set(list);
      this.setFirstFormOptionIfNeeded('fkTamanoEmpresa', list, item => item?.pk);
    });

    this.generalService.get('GET_DIAS_VISITA').subscribe(r => {
      const list = r ?? [];
      this.diasVisita.set(list);
      this.setFirstFormOptionIfNeeded('fkDiaVisita', list, item => item?.pk);
    });

    this.generalService.get('GET_PARAMETROS_PERIODO').subscribe({
      next: (data: any[]) => {
        (data ?? []).forEach(p => {
          if (p.codigo === 'PERIODO_TITULO') this.periodoTitulo.set(p.valor ?? 'Periodo');
          if (p.codigo === 'PERIODO_NUMERO') this.periodoLimite.set(Number(p.valor ?? 0));
        });

        if (!this.periodoLimite()) this.periodoLimite.set(12);
        this.selectFirstPeriodoIfNeeded();
      },
      error: () => {
        this.periodoTitulo.set('Periodo');
        this.periodoLimite.set(12);
        this.selectFirstPeriodoIfNeeded();
      }
    });
  }

  periodos(): number[] {
    const limite = this.periodoLimite();
    return limite > 0 ? Array.from({ length: limite }, (_, i) => i + 1) : [];
  }

  openMobileSelect(
    controlName: MobileSelectControlName,
    title: string,
    options: MobileSelectOption[]
  ): void {
    this.mobileSelectControl.set(controlName);
    this.mobileSelectTitle.set(title);
    this.mobileSelectOptions.set(options);
    this.mobileSelectQuery.set('');
    this.mobileSelectOpen.set(true);
  }

  closeMobileSelect(): void {
    this.mobileSelectOpen.set(false);
    this.mobileSelectControl.set(null);
    this.mobileSelectTitle.set('');
    this.mobileSelectOptions.set([]);
    this.mobileSelectQuery.set('');
  }

  updateMobileSelectQuery(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.mobileSelectQuery.set(input?.value ?? '');
  }

  applyMobileSelectOption(option: MobileSelectOption): void {
    const controlName = this.mobileSelectControl();
    if (!controlName) {
      return;
    }

    this.form.patchValue({ [controlName]: option.value } as any);

    if (controlName === 'fkDepartamento') {
      this.onDepartamentoChange();
    } else if (controlName === 'fkMunicipio') {
      this.onMunicipioChange();
    }

    this.closeMobileSelect();
  }

  isActiveMobileSelectOption(option: MobileSelectOption): boolean {
    const controlName = this.mobileSelectControl();
    if (!controlName) {
      return false;
    }

    return String(this.form.get(controlName)?.value ?? '') === String(option.value);
  }

  getMobileSelectionLabel(controlName: MobileSelectControlName, placeholder: string): string {
    const currentValue = this.form.get(controlName)?.value;
    const options = this.getMobileSelectionOptions(controlName);

    if (currentValue === null || currentValue === undefined || currentValue === '' || Number(currentValue) === 0) {
      return placeholder;
    }

    const option = options.find((item) => String(item.value) === String(currentValue));
    return option?.label ?? placeholder;
  }

  getDepartamentoMobileOptions(): MobileSelectOption[] {
    return this.departaments.map((department) => ({
      value: department.id,
      label: department.nombre
    }));
  }

  getMunicipioMobileOptions(): MobileSelectOption[] {
    return this.municipalities().map((municipality) => ({
      value: municipality.id,
      label: municipality.nombre
    }));
  }

  getUsuarioMobileOptions(): MobileSelectOption[] {
    return this.usuarios().map((user) => ({
      value: Number(user?.pk ?? 0),
      label: String(user?.nombre ?? user?.descripcion ?? '').trim()
    })).filter((option) => option.value > 0 && option.label);
  }

  getPeriodoMobileOptions(): MobileSelectOption[] {
    return this.periodos().map((period) => ({
      value: period,
      label: `${this.periodoTitulo()} ${period}`
    }));
  }

  getPeriodicidadMobileOptions(): MobileSelectOption[] {
    return this.periodicidades().map((item) => ({
      value: Number(item?.pk ?? 0),
      label: String(item?.descripcion ?? '').trim()
    })).filter((option) => option.value > 0 && option.label);
  }

  getDiaVisitaMobileOptions(): MobileSelectOption[] {
    return this.diasVisita().map((item) => ({
      value: Number(item?.pk ?? 0),
      label: String(item?.descripcion ?? '').trim()
    })).filter((option) => option.value > 0 && option.label);
  }

  getActividadMobileOptions(): MobileSelectOption[] {
    return this.actividades().map((item) => ({
      value: Number(item?.pk ?? 0),
      label: String(item?.descripcion ?? '').trim()
    })).filter((option) => option.value > 0 && option.label);
  }

  getTamanoEmpresaMobileOptions(): MobileSelectOption[] {
    return this.tamanios().map((item) => ({
      value: Number(item?.pk ?? 0),
      label: String(item?.descripcion ?? '').trim()
    })).filter((option) => option.value > 0 && option.label);
  }

  isInvalid(name: string): boolean {
    const c = this.form.get(name);
    return !!(c && c.invalid && (c.dirty || c.touched));
  }

  getErrorText(name: string): string {
    const c = this.form.get(name);
    if (!c || !c.errors) return '';

    if (c.errors['required']) return 'Campo obligatorio.';
    if (c.errors['email']) return 'Email no válido.';
    if (c.errors['pattern']) {
      if (name === 'telefonos') return 'Puedes usar números, letras, espacios y guiones.';
      if (name === 'nit') return 'El NIT puede incluir letras y números, por ejemplo CF, 4564854K o ND.';
      return 'Formato inválido.';
    }
    return 'Valor inválido.';
  }

  private buildMissingFields(): string[] {
    const out: string[] = [];

    Object.keys(this.labels).forEach(key => {
      const c = this.form.get(key);
      if (!c) return;

      if (c.errors?.['required']) out.push(this.labels[key]);
    });

    if (this.form.get('email')?.errors?.['email']) out.push('Email no válido');
    if (this.form.get('telefonos')?.errors?.['pattern']) out.push('Teléfonos (solo letras, números y signos comunes)');
    if (this.form.get('nit')?.errors?.['pattern']) out.push('NIT (puede incluir letras y números)');

    if (this.form.errors?.['primeraJornadaInvalida']) out.push('Primera jornada (hora final mayor a inicial)');
    if (this.form.errors?.['segundaJornadaInvalida']) out.push('Segunda jornada (hora final mayor a inicial)');
    if (this.form.errors?.['traslapeJornadas']) out.push('Jornadas (sin traslape)');

    if (this.pendingContacts.length === 0) out.push('Agregar al menos un contacto');
    if ((this.articleCmp?.buildArticulosPayload()?.length ?? 0) === 0) out.push('Agregar al menos un artículo válido');

    return Array.from(new Set(out));
  }

  private openValidationModal(fields: string[]): void {
    this.missingFields.set(Array.from(new Set(fields)));
    this.showValidationModal.set(this.missingFields().length > 0);
  }

  private nowLocalDateTime(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private resolveEmail(value: string | null | undefined): string {
    const email = String(value ?? '').trim();
    return email || DEFAULT_EMAIL;
  }

  private normalizePhoneText(value: unknown): string {
    return String(value ?? '').trim();
  }

  private normalizeNit(value: unknown): string {
    return String(value ?? '').trim().toUpperCase();
  }

  private applyDefaultEmail(control: AbstractControl | null): void {
    if (!control) return;
    control.setValue(this.resolveEmail(control.value as string | null | undefined), { emitEvent: false });
  }

  applyDefaultMainEmail(): void {
    this.applyDefaultEmail(this.form.get('email'));
  }

  applyDefaultContactEmail(): void {
    this.applyDefaultEmail(this.contactForm.get('email'));
  }

  preventInvalidIntegerInput(event: KeyboardEvent): void {
    if (['e', 'E', '+', '-', '.', ','].includes(event.key)) {
      event.preventDefault();
    }
  }

  normalizeNonNegativeInteger(controlName: 'ordenRuta'): void {
    const control = this.form.get(controlName);
    if (!control) return;

    const numericValue = Number(control.value);
    const safeValue = Number.isFinite(numericValue) && numericValue >= 0
      ? Math.trunc(numericValue)
      : 0;

    control.patchValue(safeValue as any, { emitEvent: false });
  }

  useCurrentLocation(): void {
    if (this.geoLoading()) return;

    if (!navigator.geolocation) {
      this.error.set('El navegador no soporta geolocalización.');
      return;
    }

    this.geoLoading.set(true);
    this.error.set(null);

    navigator.geolocation.getCurrentPosition(
      pos => {
        this.form.patchValue({
          gpsLatitud: pos.coords.latitude,
          gpsLongitud: pos.coords.longitude,
          gpsAltitud: pos.coords.altitude ?? 0,
          gpsFecha: this.nowLocalDateTime(),
        });
        this.geoLoading.set(false);
      },
      () => {
        this.geoLoading.set(false);
        this.error.set('No se pudo obtener la ubicación.');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }

  generateSourceNumber(silent = false): void {
    if (this.generatingNumber()) return;

    this.generatingNumber.set(true);
    if (!silent) {
      this.error.set(null);
    }

    this.sourceService.generateNumber()
      .pipe(finalize(() => this.generatingNumber.set(false)))
      .subscribe({
        next: res => {
          const number = Number(res?.number ?? 0);
          if (!number) {
            if (!silent) {
              this.error.set('No se pudo generar el número de fuente.');
            }
            return;
          }

          this.form.patchValue({ codigoINE: String(number) }, { emitEvent: false });
        },
        error: () => {
          if (!silent) {
            this.error.set('Error al generar el número de fuente.');
          }
        }
      });
  }

  onDepartamentoChange(): void {
    const depId = Number(this.form.get('fkDepartamento')?.value);

    this.codigosPostales.set([]);
    this.codigoPostalDescripcion = '';
    this.form.patchValue({ fkMunicipio: null, fkCodigoPostal: null });

    if (!depId) {
      this.municipalities.set([]);
      return;
    }

    this.municipalityService.getMunicipalityByDepartament(depId).subscribe({
      next: (data: any[]) => {
        const validIds = (data ?? [])
          .map((m: any) => Number(m.pk ?? m.id ?? m.municipioId))
          .filter((n: number) => !Number.isNaN(n));

        const municipalities = Object.entries(MunicipalityGT)
          .filter(([_, id]) => validIds.includes(Number(id)))
          .map(([name, id]) => ({ id: Number(id), nombre: name }));

        this.municipalities.set(municipalities);

        const selectedMunicipality = this.setFirstFormOptionIfNeeded(
          'fkMunicipio',
          municipalities,
          item => item.id
        );

        if (selectedMunicipality != null) {
          this.onMunicipioChange();
        } else {
          this.form.patchValue({ fkMunicipio: null, fkCodigoPostal: null }, { emitEvent: false });
          this.codigosPostales.set([]);
          this.codigoPostalDescripcion = '';
        }
      },
      error: () => {
        this.municipalities.set([]);
        this.error.set('No se pudieron cargar los municipios.');
      }
    });
  }

  onMunicipioChange(): void {
    const municipioId = Number(this.form.get('fkMunicipio')?.value);
    const depId = Number(this.form.get('fkDepartamento')?.value);
    const zona = Number(this.form.get('zona')?.value);

    if (!municipioId || !depId) {
      this.codigosPostales.set([]);
      this.form.patchValue({ fkCodigoPostal: null });
      this.codigoPostalDescripcion = '';
      return;
    }

    const referencia = zona && zona > 0 ? `Zona ${zona}` : '0';
    this.codigoPostalQuery$.next({ depId, municipioId, referencia });
  }

  onZonaChange(): void {
    this.onMunicipioChange();
  }

  private getMobileSelectionOptions(controlName: MobileSelectControlName): MobileSelectOption[] {
    switch (controlName) {
      case 'fkDepartamento':
        return this.getDepartamentoMobileOptions();
      case 'fkMunicipio':
        return this.getMunicipioMobileOptions();
      case 'fkUsuario':
        return this.getUsuarioMobileOptions();
      case 'periodo':
        return this.getPeriodoMobileOptions();
      case 'fkPeriodicidadVisita':
        return this.getPeriodicidadMobileOptions();
      case 'fkDiaVisita':
        return this.getDiaVisitaMobileOptions();
      case 'requiereAudio':
      case 'requiereImagen':
        return this.yesNoOptions;
      case 'fkActividadEmpresa':
        return this.getActividadMobileOptions();
      case 'fkTamanoEmpresa':
        return this.getTamanoEmpresaMobileOptions();
      case 'estado':
        return this.statusOptions;
      default:
        return [];
    }
  }

  private normalizeSearchText(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  private normalize(v?: string | null): string {
    return (v ?? '').trim().toLowerCase();
  }

  private contactKey(c: SourceContact): string {
    const email = this.normalize(c.email);
    if (email && email !== DEFAULT_EMAIL) return `email:${email}`;
    return [
      this.normalize(c.name),
      this.normalize(c.phones),
      this.normalize(c.position),
      this.normalize(c.title)
    ].join('|');
  }

  onContactAdded(draft: BaseContact): void {
    const name = (draft.name ?? '').trim();
    const email = (draft.email ?? '').trim();
    const phones = this.normalizePhoneText(draft.phones);
    const order = Number(draft.order);

    if (!name) {
      this.error.set('El contacto debe tener nombre.');
      return;
    }

    if (!Number.isFinite(order) || order < 0) {
      this.error.set('El orden del contacto es inválido.');
      return;
    }

    if (phones && !PHONE_TEXT_PATTERN.test(phones)) {
      this.error.set('El teléfono del contacto puede incluir números, letras, espacios y guiones.');
      return;
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.error.set('El email del contacto no es válido.');
      return;
    }

    const contact: SourceContact = {
      sourceId: 0,
      name,
      title: (draft.title ?? '').trim(),
      position: (draft.position ?? '').trim(),
      phones,
      email: this.resolveEmail(email),
      comments: (draft.comments ?? '').trim(),
      order,
      status: draft.status ?? 'A'
    };

    if (this.editingIndex !== null) {
      const existsInOther = this.pendingContacts.some((c, idx) =>
        idx !== this.editingIndex && this.contactKey(c) === this.contactKey(contact)
      );

      if (existsInOther) {
        this.error.set('Ese contacto ya existe.');
        return;
      }

      const updated = [...this.pendingContacts];
      updated[this.editingIndex] = contact;
      this.pendingContacts = updated.sort((a, b) => a.order - b.order);
      this.editingIndex = null;
      this.error.set(null);
      return;
    }

    const exists = this.pendingContacts.some(c => this.contactKey(c) === this.contactKey(contact));
    if (exists) {
      this.error.set('Ese contacto ya existe.');
      return;
    }

    this.pendingContacts = [...this.pendingContacts, contact].sort((a, b) => a.order - b.order);
    this.error.set(null);
  }

  removePendingContact(i: number): void {
    this.pendingContacts = this.pendingContacts.filter((_, idx) => idx !== i);
  }



  onSubmit(): void {
    if (this.loading() || this.success()) return;

    const userId = this.getLoggedUserId();
    if (!userId) {
      this.error.set('Sesión no válida. Inicie sesión nuevamente.');
      return;
    }

    this.success.set(false);
    this.showValidationModal.set(false);
    this.error.set(null);
    this.missingFields.set([]);

    this.form.markAllAsTouched();

    this.applyDefaultEmail(this.form.get('email'));

    const validationIssues = this.buildMissingFields();
    if (validationIssues.length > 0) {
      this.openValidationModal(validationIssues);
      return;
    }

    this.loading.set(true);

    const raw: any = this.form.getRawValue();

    const payload: SourceSurveyPayload = {
      fuente: {
        codigoINE: (raw.codigoINE ?? '').trim(),
        razonSocial: (raw.razonSocial ?? '').trim(),
        nombre: (raw.nombre ?? '').trim(),
        direccion: (raw.direccion ?? '').trim(),
        fkCodigoPostal: Number(raw.fkCodigoPostal),
        fkDepartamento: Number(raw.fkDepartamento),
        fkMunicipio: Number(raw.fkMunicipio),
        periodo: Number(raw.periodo),
        ordenRuta: raw.ordenRuta != null && raw.ordenRuta !== '' ? Number(raw.ordenRuta) : 0,
        zona: raw.zona != null && raw.zona !== '' ? Number(raw.zona) : 0,
        gpsLatitud: raw.gpsLatitud ?? null,
        gpsLongitud: raw.gpsLongitud ?? null,
        gpsAltitud: raw.gpsAltitud ?? 0,
        gpsFecha: raw.gpsFecha ?? null,
        telefonos: this.normalizePhoneText(raw.telefonos),
        email: this.resolveEmail(raw.email),
        fkUsuario: Number(raw.fkUsuario),
        fkPeriodicidadVisita: Number(raw.fkPeriodicidadVisita),
        fkDiaVisita: Number(raw.fkDiaVisita),
        horaInicioPrimeraJornada: raw.horaInicioPrimeraJornada ?? '',
        horaFinalPrimeraJornada: raw.horaFinalPrimeraJornada ?? '',
        horaInicioSegundaJornada: raw.horaInicioSegundaJornada ?? '',
        horaFinalSegundaJornada: raw.horaFinalSegundaJornada ?? '',
        requiereAudio: raw.requiereAudio ?? 'N',
        requiereImagen: raw.requiereImagen ?? 'N',
        nit: this.normalizeNit(raw.nit),
        fkActividadEmpresa: raw.fkActividadEmpresa != null && raw.fkActividadEmpresa !== '' ? Number(raw.fkActividadEmpresa) : null,
        fkTamanoEmpresa: raw.fkTamanoEmpresa != null && raw.fkTamanoEmpresa !== '' ? Number(raw.fkTamanoEmpresa) : null,
        codigoCIIU: (raw.codigoCIIU ?? '').trim(),
        comentarios: (raw.comentarios ?? '').trim(),
        referencia: (raw.referencia ?? '').trim(),
        informacionAdicional: (raw.informacionAdicional ?? '').trim(),
        estado: raw.estado ?? 'P'
      },
      contactos: (this.pendingContacts ?? []).map(contact => ({
        title: (contact.title ?? '').trim(),
        name: (contact.name ?? '').trim(),
        position: (contact.position ?? '').trim(),
        phones: this.normalizePhoneText(contact.phones),
        email: this.resolveEmail(contact.email),
        comments: (contact.comments ?? '').trim(),
        order: Number(contact.order ?? 0),
        status: (contact.status ?? 'P') as 'A' | 'I' | 'P'
      })),
      articulos: this.articleCmp?.buildArticulosPayload() ?? [],
      segUsuario: userId,
      segTransaccion: 3
    };

    this.surveyService.saveSurvey(payload)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res: SourceSurveyResponse) => {
          const ok = this.readSurveyOk(res);
          if (ok !== 1) {
            this.error.set(this.readSurveyMessage(res));
            return;
          }

          const sourceId = Number(res?.fk_fuente ?? res?.FK_FUENTE ?? 0);
          this.createdSourceId.set(sourceId || null);
          this.resetAll();
          this.success.set(true);
        },
        error: (err: HttpErrorResponse) => {
          console.error('Error al guardar levantamiento desde add-source', { payload, err });
          const message = this.readSurveyHttpError(err);
          this.articleCmp?.handleSaveFailure(message);
          this.error.set(message);
        }
      });
  }

  closeSuccessModal(): void {
    this.success.set(false);
  }

  closeValidationModal(): void {
    this.showValidationModal.set(false);
  }

  private readSurveyOk(res: SourceSurveyResponse | null | undefined): number {
    return Number(res?.ok ?? res?.OK ?? 0);
  }

  private readSurveyMessage(res: SourceSurveyResponse | null | undefined): string {
    const mensaje = res?.mensaje ?? res?.MENSAJE;
    const detail = res?.detail ?? res?.DETAIL;

    if (mensaje && detail) {
      return `${mensaje}: ${detail}`;
    }

    return mensaje ?? detail ?? 'Error al guardar el levantamiento de la fuente.';
  }

  private readSurveyHttpError(err: HttpErrorResponse): string {
    const body = err?.error;

    if (typeof body === 'string' && body.trim()) {
      return body;
    }

    if (body && typeof body === 'object') {
      return this.readSurveyMessage(body as SourceSurveyResponse);
    }

    return err.message || 'Error al guardar el levantamiento de la fuente.';
  }

  private saveContactsSequentially(
    sourceId: number,
    userId: number,
    pendingArticles: SurveyArticulo[],
    index: number
  ): void {
    if (index >= this.pendingContacts.length) {
      const articleRequests = this.expandArticleRequests(sourceId, userId, pendingArticles);
      this.saveArticlesSequentially(articleRequests, 0);
      return;
    }

    const c = this.pendingContacts[index];

    const payload = {
      sourceId,
      title: (c.title ?? '').trim(),
      name: (c.name ?? '').trim(),
      position: (c.position ?? '').trim(),
      phones: (c.phones ?? '').trim(),
      email: this.resolveEmail(c.email),
      comments: (c.comments ?? '').trim(),
      order: Number(c.order ?? 0),
      status: c.status ?? 'A'
    };

    this.sourceContactService.create(payload as any, userId)
      .pipe(finalize(() => { }))
      .subscribe({
        next: () => this.saveContactsSequentially(sourceId, userId, pendingArticles, index + 1),
        error: () => {
          this.loading.set(false);
          this.error.set(`La fuente se guardó, pero hubo error al guardar el contacto #${index + 1}.`);
        }
      });
  }

  private expandArticleRequests(sourceId: number, userId: number, articles: SurveyArticulo[]): any[] {
    return (articles ?? []).flatMap(article =>
      (article.characteristics ?? []).map(characteristic => ({
        sourceId,
        articleId: Number(article.articleId),
        unitMeasureId: Number(article.unitMeasureId),
        cantidad: Number(article.cantidad ?? 0),
        precio: Number(article.precio ?? 0),
        comments: (article.comments ?? '').trim(),
        characteristicId: Number(characteristic.characteristicId),
        characteristicValueCode:
          characteristic.characteristicValueCode != null
            ? Number(characteristic.characteristicValueCode)
            : undefined,
        value: (characteristic.value ?? '').trim(),
        auditUser: userId,
        auditTransaction: 3
      }))
    );
  }

  private saveArticlesSequentially(requests: any[], index: number): void {
    if (index >= requests.length) {
      this.loading.set(false);
      this.resetAll();
      this.success.set(true);
      setTimeout(() => this.success.set(false), 4000);
      return;
    }

    this.sourceArticlesService.create({
      fkFuente: Number(requests[index].sourceId),
      fkArticulo: Number(requests[index].articleId),
      fkUnidadMedida: Number(requests[index].unitMeasureId),
      cantidad: Number(requests[index].cantidad ?? 0),
      precio: Number(requests[index].precio ?? 0),
      comentarios: (requests[index].comments ?? '').trim(),
      estado: 'A',
      fkUsuario: Number(requests[index].auditUser ?? 0),
      fkTransaccion: Number(requests[index].auditTransaction ?? 3)
    }).subscribe({
      next: () => this.saveArticlesSequentially(requests, index + 1),
      error: () => {
        this.loading.set(false);
        this.error.set(`La fuente se guardÃ³, pero hubo error al guardar el artÃ­culo #${index + 1}.`);
      }
    });
  }

  resetAll(): void {
    this.pendingContacts = [];
    this.editingIndex = null;
    this.editingContactIndex = null;
    this.showContactForm.set(false);
    this.createdSourceId.set(null);

    this.error.set(null);
    this.missingFields.set([]);
    this.showValidationModal.set(false);

    this.form.reset({
      requiereAudio: 'N',
      requiereImagen: 'N',
      estado: 'P',
      gpsFecha: this.nowLocalDateTime(),
      telefonos: '',
      nit: '',
      email: '',
      fkDepartamento: null,
      fkMunicipio: null,
      fkCodigoPostal: null,
      fkUsuario: null,
      fkPeriodicidadVisita: null,
      fkDiaVisita: null,
      periodo: null,
      ordenRuta: 0,
      fkActividadEmpresa: null,
      fkTamanoEmpresa: null,
      zona: null,
      ...DEFAULT_JORNADAS
    });

    this.codigoPostalDescripcion = '';
    this.codigosPostales.set([]);
    this.municipalities.set([]);
    this.contactForm.reset({
      order: 1,
      status: 'P'
    });
    this.articleCmp?.reset();
  }

  onCancel(): void {
    if (this.loading()) return;

    if ((this.form.dirty || this.pendingContacts.length > 0 || (this.articleCmp?.buildArticulosPayload()?.length ?? 0) > 0) && !this.success()) {
      const ok = confirm('Hay información sin guardar. ¿Desea salir al inicio?');
      if (!ok) return;
    }

    this.router.navigate(['/home']);
  }

  contactForm = this.fb.group({
    name: ['', [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(100),
      Validators.pattern(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/)
    ]],

    position: ['', [Validators.maxLength(100)]],

    phones: ['', [
      Validators.maxLength(50),
      Validators.pattern(PHONE_TEXT_PATTERN)
    ]],

    email: ['', [
      Validators.email,
      Validators.maxLength(150)
    ]],

    order: [1, [
      Validators.required,
      Validators.min(1),
      Validators.max(100)
    ]],

    comments: [''],
    title: [''],
    status: ['P']
  });


  addContact(): void {

    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    const v = this.contactForm.value;

    const contact: SourceContact = {
      sourceId: 0,
      name: (v.name ?? '').trim(),
      title: (v.title ?? '').trim(),
      position: (v.position ?? '').trim(),
      phones: this.normalizePhoneText(v.phones),
      email: this.resolveEmail(v.email),
      comments: (v.comments ?? '').trim(),
      order: Number(v.order ?? 0),
      status: v.status ?? 'A'
    };

    // MODO EDICIÓN
    if (this.editingContactIndex !== null) {

      const updated = [...this.pendingContacts];

      updated[this.editingContactIndex] = contact;

      this.pendingContacts = updated.sort((a, b) => a.order - b.order);

      this.editingContactIndex = null;

    } else {

      const exists = this.pendingContacts.some(c => this.contactKey(c) === this.contactKey(contact));

      if (exists) {
        this.error.set('Ese contacto ya existe.');
        return;
      }

      this.pendingContacts = [...this.pendingContacts, contact]
        .sort((a, b) => a.order - b.order);
    }

    this.contactForm.reset({
      order: 1,
      status: 'P'
    });

  }


  toggleContactForm(): void {
    this.showContactForm.set(!this.showContactForm());
  }

  editContact(contact: SourceContact, index: number): void {

    this.editingContactIndex = index;

    this.contactForm.patchValue({
      name: contact.name,
      position: contact.position,
      phones: contact.phones,
      email: contact.email,
      order: contact.order,
      comments: contact.comments,
      title: contact.title,
      status: contact.status
    });

    this.showContactForm.set(true);

  }


}
